import { useRef } from 'react'
import { useNavigation } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { api } from '@api'
import { useFtConversationAiGenerate, useInitiateIndividualConversation } from '@hooks'
import { useLanguage } from '@context'
import { useMessageDraftStore } from '@store'
import { showApiErrorToast } from '@utils'
import {
  birthdayWishFallback,
  birthdayWishInstruction,
  pickBirthdayWish,
  type BirthdayWishTarget,
} from './birthdayWish'

/** Past this, the AI wish is dropped for the template (the composer waits). */
const AI_WISH_TIMEOUT_MS = 20_000

/**
 * A birthday wish written by the AI from the conversation's last messages,
 * the plain template otherwise, as if written at `at` (now, or the moment a
 * scheduled wish leaves). Never rejects.
 */
const useWriteBirthdayWish = () => {
  const { t, currentLanguage } = useLanguage()
  const { mutateAsync: generateSuggestions } = useFtConversationAiGenerate()

  return async (item: BirthdayWishTarget, conversationId: string, at: Dayjs = dayjs()): Promise<string> => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      const { suggestions } = await Promise.race([
        generateSuggestions({
          conversationId,
          instruction: birthdayWishInstruction(item, at, currentLanguage),
        }),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('AI wish timeout')), AI_WISH_TIMEOUT_MS)
        }),
      ])
      const wish = pickBirthdayWish(suggestions)
      if (wish) return wish
    } catch {
      // No AI key (local), quota, network, timeout: the template below.
    } finally {
      clearTimeout(timeout)
    }
    return birthdayWishFallback(item, at, t)
  }
}

/**
 * « Lui souhaiter » : opens the conversation with the person at once, then
 * drops a birthday wish in its composer, written by the AI from the last
 * messages (the plain template otherwise). Used by the Birthdays page and the
 * Notifications page. `id` is the person's account id: a ghost profile can't
 * receive messages.
 */
export const useSendBirthdayWish = () => {
  const navigation = useNavigation()
  const { t } = useLanguage()
  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()
  const writeBirthdayWish = useWriteBirthdayWish()
  const startDraft = useMessageDraftStore((s) => s.startDraft)
  const fillDraft = useMessageDraftStore((s) => s.fillDraft)
  const isOpeningConversation = useRef(false)

  return async (item: BirthdayWishTarget & { id: string }) => {
    if (isOpeningConversation.current) return
    isOpeningConversation.current = true
    let conversationId: string
    try {
      conversationId = (await initiateIndividualConversation({ participantAccountId: item.id })).id
    } catch (err) {
      showApiErrorToast(t, err)
      return
    } finally {
      isOpeningConversation.current = false
    }
    startDraft(conversationId)
    ;(navigation as any).navigate('Messages', {
      screen: 'Conversation',
      params: { conversationId },
    })
    fillDraft(conversationId, await writeBirthdayWish(item, conversationId))
  }
}

/**
 * « Programmer mes vœux » (the day before a birthday): the wish is written
 * now, as it will read when it leaves, and scheduled for `sendAt`. Returns
 * the id of the scheduled message, so it can be read and changed.
 */
export const useScheduleBirthdayWish = () => {
  const queryClient = useQueryClient()
  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()
  const writeBirthdayWish = useWriteBirthdayWish()

  return async (item: BirthdayWishTarget & { id: string }, sendAt: Dayjs): Promise<string> => {
    const conversationId = (await initiateIndividualConversation({ participantAccountId: item.id })).id
    const message = await writeBirthdayWish(item, conversationId, sendAt)
    const { data: scheduledMessageId } = await api.post<string>('/scheduled-message', {
      message,
      recipientAccountId: item.id,
      scheduledDate: sendAt.toISOString(),
      reason: 'birthday',
    })
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scheduled-messages'] }),
      queryClient.invalidateQueries({ queryKey: [`scheduled-messages-by-recipient-${item.id}`] }),
      queryClient.invalidateQueries({ queryKey: ['notifications'], refetchType: 'all' }),
    ])
    return scheduledMessageId
  }
}
